// Каталог событий — по specs/001-service-foundation/contracts/events.md и новым событиям
// specs/002-platform/contracts/events.md. С 002 событие пишется в kernel.events в транзакции
// изменения и доставляется подписчикам; уведомления по-прежнему создаются в той же транзакции.
// Данные события — только номера записей, даты и значения перечислений, без ФИО и почты: схемы
// строгие, лишнее поле отклоняется. Тексты уведомлений — дословно из events.md и ui.md.
import { z } from '../kernel/zod.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { JobTypeSchema, eventSubscribers } from '../jobs/catalog.ts';
import type { EventSubscriber } from '../jobs/catalog.ts';
import { DateSchema, IdSchema, TimestampSchema } from '../kernel/common.ts';
import { RoleSchema } from '../rights/roles.ts';
import { ProtocolRoleSchema } from '../minutes/access.ts';
import { LoginLinkPurposeSchema } from '../auth/link.ts';
import { RevisionCommandKindSchema } from '../minutes/revisions.ts';

/**
 * Кому уведомление: администраторам клиента (ролевое), заместителю, владельцу роли,
 * сопровождению; с 002 — заказавшему выгрузку, роли «контролёр» и роли «сопровождение»
 * (ролевые), выдавшему временный доступ администратору (лично, если ролевое
 * «администраторам клиента» ему уже не видно — без второй копии).
 */
export const notificationRecipients = [
  'client_admins',
  'deputy',
  'owner',
  'support_user',
  'requester',
  'controllers',
  'support',
  'granted_by',
  // 030: ведущий протокола — его владелец (ADR-0039): «Назначение оспорено», «Ошибка в пункте».
  'protocol_lead',
  // 031: люди пункта по правилам поводов (control/notices): ответственные после ревизии, снятые,
  // основной куратор — записи справочника, с учётной записью или без (ADR-0046).
  'item_people',
] as const;
export type NotificationRecipient = (typeof notificationRecipients)[number];

/**
 * Куда ведёт щелчок (ui.md): user — карточка пользователя из данных события,
 * profile — «Профиль», substitutions — «Замещения», support_access — «Доступ сопровождения»,
 * audit_exports — «Мои выгрузки», audit_export — «Мои выгрузки» на заказе из данных события
 * (строка выделена, фокус — на её действии), ops_alerts — «Оповещения» (002).
 */
export const notificationLinks = {
  user: '/users/{id}',
  profile: '/profile',
  substitutions: '/substitutions',
  support_access: '/support-access',
  audit_exports: '/audit/exports',
  audit_export: '/audit/exports?focus={id}',
  ops_alerts: '/ops/alerts',
  // 030: пункт в таблице протокола и «Справочник → Вопросы».
  protocol_item: '/protocols/{id}?item={itemId}',
  protocol: '/protocols/{id}',
  directory_questions: '/directory?tab=questions',
  // 032: «Входящие» на строке уведомления и «Просрочено у ваших сотрудников».
  inbox: '/inbox',
  team: '/team',
} as const;
export type NotificationLink = keyof typeof notificationLinks;

export interface NotificationRule {
  readonly to: readonly NotificationRecipient[];
  /**
   * Текст с метками: {роль} — роль внутри фразы, {дата} — дд.мм.гггг, {время} — чч:мм, {ФИО},
   * {N} — число, {подпись} — подпись расписания; 030 — {пункт} (номер пункта «3.2»),
   * {протокол} (номер протокола «П-12»).
   */
  readonly title: string;
  /** Куда ведёт; нет — уведомление без перехода. */
  readonly link?: NotificationLink;
  /** Кнопка под текстом: подпись глаголом и куда ведёт (002, FR-053а). */
  readonly action?: { readonly label: string; readonly link: NotificationLink };
  /** Когда именно, если у события несколько уведомлений. */
  readonly variant?: string;
}

/**
 * Почему пропущен запуск расписания (FR-014, FR-012а): stale — старше окна актуальности,
 * limit — сверх 10 запусков догона за раз. Для контролёра оба — «сервис не работал».
 */
export const scheduleSkipReasons = ['stale', 'limit'] as const;
export const ScheduleSkipReasonSchema = z.enum(scheduleSkipReasons);

export interface EventDefinition {
  /** Когда — дословно из events.md. */
  readonly when: string;
  readonly data: z.ZodObject;
  readonly notifications: readonly NotificationRule[];
}

const toAdminsAndSupport = ['support_user', 'client_admins'] as const;

export const eventCatalog = {
  'user.created': {
    when: 'создан пользователь',
    data: z.strictObject({ userId: IdSchema }),
    notifications: [],
  },
  'user.archived': {
    when: 'пользователь в архиве',
    data: z.strictObject({ userId: IdSchema }),
    notifications: [
      {
        variant: 'пользователь был заместителем',
        to: ['client_admins'],
        title: 'Замещение закончилось: заместитель в архиве',
        link: 'user',
      },
      {
        variant: 'пользователь — владелец роли с действующим замещением',
        to: ['client_admins'],
        title: 'Владелец роли в архиве: назначьте роль {роль} насовсем',
        link: 'user',
      },
    ],
  },
  'user.restored': {
    when: 'пользователь возвращён',
    data: z.strictObject({ userId: IdSchema }),
    notifications: [],
  },
  'user.locked': {
    when: 'блокировка после 5 попыток',
    data: z.strictObject({ userId: IdSchema, lockedUntil: TimestampSchema }),
    notifications: [
      { to: ['client_admins'], title: 'Вход заблокирован до {время}: {ФИО}', link: 'user' },
    ],
  },
  'role.assigned': {
    when: 'назначена роль',
    data: z.strictObject({ assignmentId: IdSchema, userId: IdSchema, role: RoleSchema }),
    notifications: [],
  },
  'role.removed': {
    when: 'назначение в архиве',
    data: z.strictObject({ assignmentId: IdSchema, userId: IdSchema, role: RoleSchema }),
    notifications: [],
  },
  // 020: выдача доступа к протоколу с письмом и приглашение (contracts/api.md 020, «События»).
  // Только номера: событие пишет технический журнал (observability.log), имён и названий нет.
  'protocol.access_granted': {
    when: 'выдан доступ к протоколу с письмом или сотрудник приглашён',
    data: z.strictObject({
      documentId: IdSchema,
      grantId: IdSchema,
      userId: IdSchema,
      actorUserId: IdSchema,
      role: ProtocolRoleSchema,
      invite: z.boolean(),
    }),
    notifications: [],
  },
  // 025 (research Р1, Р4): только номера — почта и токен в событие не попадают.
  'password.recovery_requested': {
    when: 'пользователь запросил ссылку восстановления пароля',
    data: z.strictObject({
      userId: IdSchema,
      requestId: IdSchema,
    }),
    notifications: [],
  },
  // 035 (contracts/api.md 035, «Событие»): создание пользователя администратором и «Отправить письмо
  // заново» — только номера; почта и токен в событие не попадают.
  'user.welcome_requested': {
    when: 'запрошено приветственное письмо',
    data: z.strictObject({
      userId: IdSchema,
      actorUserId: IdSchema,
    }),
    notifications: [],
  },
  // 030 (contracts/api.md 030, «События и уведомления»): только номера и значения перечней —
  // почта, токен, код, текст вопроса и ответа в событие не попадают: текст уведомления пишет
  // обработчик в том же действии.
  'access.login_link_requested': {
    when: 'запрошена ссылка входа: «Войти по ссылке на почту» или «Прислать на n***@…»',
    data: z.strictObject({
      personId: IdSchema,
      purpose: LoginLinkPurposeSchema,
      linkRequestId: IdSchema,
    }),
    notifications: [],
  },
  'access.login_link_not_me': {
    when: 'на странице ссылки входа нажали «Это не я»',
    data: z.strictObject({ personId: IdSchema }),
    notifications: [
      {
        to: ['controllers'],
        title: 'Ссылку входа переслали: человек нажал «Это не я»',
      },
    ],
  },
  'assignee.help': {
    when: 'пользователь отправил вопрос из «Помощи»',
    data: z.strictObject({ userId: IdSchema, itemId: IdSchema.optional() }),
    notifications: [{ to: ['controllers'], title: 'Вопрос из «Помощи»: {ФИО}' }],
  },
  'item.link_disputed': {
    when: 'ответственный выбрал «Ошибка в пункте…» → «Не мой пункт»',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema, checkId: IdSchema }),
    notifications: [
      {
        to: ['protocol_lead', 'controllers'],
        title: 'Назначение оспорено: пункт {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.reported': {
    when: 'ответственный сообщил об ошибке в тексте или сроке пункта',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['protocol_lead', 'controllers'],
        title: 'Ошибка в пункте {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  // 031: событие ревизии протокола и уведомления о пунктах (contracts/api.md 031, §5; ADR-0046).
  // Событие — одно на ревизию, только номера; что изменилось, правила читают из истории пунктов.
  // Уведомления о пунктах пишет подписчик control.itemNotices: получатели — люди пункта (запись
  // справочника), ведущий протокола, основной куратор; автору — нет. Данные — номера.
  'minutes.revision_applied': {
    when: 'принята ревизия протокола: команда таблицы, перевод, возврат, создание, системная правка',
    data: z.strictObject({
      documentId: IdSchema,
      revision: z.number().int().min(0),
      commandKind: RevisionCommandKindSchema.nullable(),
      actorUserId: IdSchema,
    }),
    notifications: [],
  },
  'item.assigned': {
    when: 'человека назначили ответственным пункта',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      { to: ['item_people'], title: 'Назначили: пункт {пункт}, {протокол}', link: 'protocol_item' },
    ],
  },
  'item.assigned_many': {
    when: 'перевод, вставка строк или связь упоминания назначили человеку несколько пунктов',
    data: z.strictObject({ documentId: IdSchema, count: z.number().int().min(1) }),
    notifications: [
      { to: ['item_people'], title: 'Вам поручено {N} пунктов в {протокол}', link: 'protocol' },
    ],
  },
  'item.unassigned': {
    when: 'человека сняли с пункта',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Вас сняли с пункта {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.due_changed': {
    when: 'срок пункта изменил не сам ответственный',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Изменили срок пункта {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.title_changed': {
    when: '«что сделать» пункта изменил не сам ответственный',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Изменили «что сделать» в пункте {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.reopened': {
    when: 'пункт вернули из «Выполнено» или «Отменено»',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Пункт {пункт} вернули в работу, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.archived': {
    when: 'пункт перенесли в архив',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      { to: ['item_people'], title: 'Пункт {пункт} в архиве, {протокол}', link: 'protocol_item' },
    ],
  },
  'item.restored': {
    when: 'пункт вернули из архива',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Пункт {пункт} вернули из архива, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.answered': {
    when: 'ответственный поставил новый срок или «Выполнено»',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['protocol_lead'],
        title: 'Ответственный ответил по пункту {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.due_soon': {
    when: 'до срока открытого пункта осталось N рабочих дней',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Срок через {N} рабочих дня: пункт {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item.overdue': {
    when: 'срок открытого пункта прошёл',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Просрочено: пункт {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'item_letter.failed': {
    when: 'письмо о пунктах не ушло после всех попыток',
    data: z.strictObject({ messageId: IdSchema }),
    notifications: [{ to: ['controllers'], title: 'Письмо о пунктах не ушло: № {N}' }],
  },
  'first_letter.replied': {
    when: 'пришло ответное письмо на «Первое письмо»',
    data: z.strictObject({ messageId: IdSchema, documentId: IdSchema }),
    notifications: [
      { to: ['controllers'], title: 'Ответ на «Первое письмо» по {протокол}', link: 'protocol' },
    ],
  },
  // 032: ответ из письма, ответное письмо, копия руководителю (contracts/api.md 032, §4; ADR-0047).
  // В данных — только номера; текст — шаблон каталога.
  'answer.accepted': {
    when: 'ответ со страницы ответа применён',
    data: z.strictObject({ answerId: IdSchema, documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Ответ принят: пункт {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'answer.not_applied': {
    when: 'ответ со страницы ответа не применён: пункт изменился после письма',
    data: z.strictObject({ answerId: IdSchema, documentId: IdSchema, itemId: IdSchema }),
    notifications: [{ to: ['item_people'], title: 'Ответ не применён: пункт {пункт}, {протокол}' }],
  },
  'answer.handed': {
    when: 'ответ из письма передан контролёру: устарел, пункт закрыт, автора нет или проверка',
    data: z.strictObject({ answerId: IdSchema, documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['controllers'],
        title: 'Ответ из письма не применён: пункт {пункт}, {протокол}',
        link: 'inbox',
      },
    ],
  },
  'answer.contractor': {
    when: 'сотрудник внешней компании ответил по пункту',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Ответ подрядчика: пункт {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'reply.received': {
    when: 'пришло ответное письмо на письмо о пунктах',
    data: z.strictObject({ replyId: IdSchema, messageId: IdSchema }),
    notifications: [
      { to: ['controllers'], title: 'Ответное письмо на письмо о пунктах', link: 'inbox' },
    ],
  },
  'item.manager_copied': {
    when: 'копия о просроченном пункте ушла руководителю ответственного',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      {
        to: ['item_people'],
        title: 'Копия ушла руководителю: пункт {пункт}, {протокол}',
        link: 'protocol_item',
      },
    ],
  },
  'team.answered': {
    when: 'ответственный ответил после копии руководителю',
    data: z.strictObject({ documentId: IdSchema, itemId: IdSchema }),
    notifications: [
      { to: ['item_people'], title: 'Ответ получен: пункт {пункт}, {протокол}', link: 'team' },
    ],
  },
  'directory.links_changed': {
    when: 'связь упоминаний с записью снята или изменена: «Не он», решение по запросу, разъединение',
    data: z.strictObject({ recordIds: z.array(IdSchema).min(1), decidedBy: IdSchema }),
    notifications: [],
  },
  'directory.records_merged': {
    when: 'записи человека склеены',
    data: z.strictObject({ mainId: IdSchema, mergedId: IdSchema }),
    notifications: [],
  },
  'directory.record_archived': {
    when: 'запись справочника отправлена в архив',
    data: z.strictObject({ recordId: IdSchema }),
    notifications: [],
  },
  'directory.org_mail_off': {
    when: 'у организации выключили «получает письма»',
    data: z.strictObject({ organizationId: IdSchema }),
    notifications: [],
  },
  'substitution.created': {
    when: 'замещение назначено',
    data: z.strictObject({
      substitutionId: IdSchema,
      assignmentId: IdSchema,
      deputyUserId: IdSchema,
      validFrom: DateSchema,
      validTo: DateSchema,
    }),
    notifications: [
      { to: ['deputy'], title: 'Вы замещаете {роль} с {дата} по {дата}', link: 'profile' },
    ],
  },
  'substitution.cancelled': {
    when: 'замещение отменено досрочно; запланированное — при архиве заместителя или снятии роли у владельца',
    data: z.strictObject({ substitutionId: IdSchema }),
    notifications: [
      { to: ['deputy', 'owner'], title: 'Замещение {роль} отменено', link: 'substitutions' },
    ],
  },
  'substitution.ended': {
    when: 'срок замещения прошёл; действующее — при архиве заместителя или снятии роли у владельца',
    data: z.strictObject({ substitutionId: IdSchema }),
    notifications: [
      { to: ['owner', 'deputy'], title: 'Замещение {роль} закончилось', link: 'substitutions' },
    ],
  },
  'support.granted': {
    when: 'выдан временный доступ',
    data: z.strictObject({
      grantId: IdSchema,
      supportUserId: IdSchema,
      validUntil: TimestampSchema,
    }),
    notifications: [
      {
        to: toAdminsAndSupport,
        title: 'Доступ сопровождения выдан до {время}',
        link: 'support_access',
      },
    ],
  },
  'support.revoked': {
    when: 'доступ прекращён досрочно',
    data: z.strictObject({ grantId: IdSchema }),
    notifications: [
      { to: toAdminsAndSupport, title: 'Доступ сопровождения прекращён', link: 'support_access' },
    ],
  },
  'support.expired': {
    when: 'срок доступа прошёл',
    data: z.strictObject({ grantId: IdSchema }),
    // С 002 — и выдавшему администратору (FR-016а).
    notifications: [
      {
        to: [...toAdminsAndSupport, 'granted_by'],
        title: 'Доступ сопровождения закончился',
        link: 'support_access',
      },
    ],
  },

  // 002: выгрузка журнала в фоне, пропущенный запуск расписания, недоставленные оповещения.
  'audit.export.ready': {
    when: 'файл выгрузки готов',
    data: z.strictObject({
      exportId: IdSchema,
      recordsCount: z.number().int().nonnegative(),
    }),
    notifications: [
      { to: ['requester'], title: 'Выгрузка журнала готова: {N} записей', link: 'audit_exports' },
    ],
  },
  'audit.export.failed': {
    when: 'выгрузка не удалась после всех повторов или сопровождение отменило её задачу',
    data: z.strictObject({ exportId: IdSchema, code: ErrorCodeSchema }),
    // Тело — причина словами, код и номер операции заказа — отдельными полями; кнопка
    // «Заказать снова» — «Мои выгрузки» на этом заказе (FR-053а; exportFailedNotice). Задачу
    // отменило сопровождение — код OPS-003 «Выгрузку отменило сопровождение» (FR-008б).
    notifications: [
      {
        to: ['requester'],
        title: 'Выгрузка журнала не удалась',
        link: 'audit_exports',
        action: { label: 'Заказать снова', link: 'audit_export' },
      },
    ],
  },
  'schedule.skipped': {
    when: 'пропущен устаревший запуск расписания',
    // dueAt — самый поздний пропущенный момент такта, firstDueAt — самый ранний, count — сколько
    // моментов пропущено (при catchUp: each — каждый, до 1000; при latest — 1).
    data: z.strictObject({
      schedule: JobTypeSchema,
      dueAt: TimestampSchema,
      reason: ScheduleSkipReasonSchema,
      count: z.number().int().positive(),
      firstDueAt: TimestampSchema,
    }),
    // Без технических подробностей и данных записей (FR-014).
    notifications: [
      {
        to: ['controllers'],
        title: 'Пропущен запуск «{подпись}» за {время}: сервис не работал',
      },
    ],
  },
  'alerts.undelivered': {
    when: 'оповещения не доходят 30 минут ни по одному настроенному каналу',
    // Только уведомление: его пишет обработчик оповещений, а у него нет прав на kernel.events
    // (FR-044д) — подписчикам событие не доставляется, строка — в техническом журнале.
    data: z.strictObject({ since: TimestampSchema }),
    // Текст называет только настроенные каналы: канал без настроек — не сбой (FR-044ж).
    notifications: [
      {
        variant: 'настроены Telegram и почта',
        to: ['support'],
        title: 'Оповещения не доходят: Telegram и почта не отвечают с {время}',
        link: 'ops_alerts',
      },
      {
        variant: 'настроен только Telegram',
        to: ['support'],
        title: 'Оповещения не доходят: Telegram не отвечает с {время}',
        link: 'ops_alerts',
      },
      {
        variant: 'настроена только почта',
        to: ['support'],
        title: 'Оповещения не доходят: почта не отвечает с {время}',
        link: 'ops_alerts',
      },
    ],
  },
} as const satisfies Record<string, EventDefinition>;

export type EventName = keyof typeof eventCatalog;

export type EventData<N extends EventName> = z.infer<(typeof eventCatalog)[N]['data']>;

export const eventNames = Object.keys(eventCatalog) as EventName[];

export const EventNameSchema = z.enum(eventNames);

export function isEventName(value: string): value is EventName {
  return Object.hasOwn(eventCatalog, value);
}

/**
 * На какие события подписан подписчик (specs/002-platform/contracts/jobs.md, «Подписчики
 * событий»): '*' — на все события каталога. По этому списку emit ставит задачи
 * kernel.event.deliver в любом процессе; обработчики подключает обработчик задач.
 */
export const eventSubscriptions: Readonly<Record<EventSubscriber, '*' | readonly EventName[]>> = {
  'observability.log': '*',
  // Пользователь в архиве — его файлы выгрузки удаляются (FR-054б). Сервер приложения том
  // файлов только читает: удаляет обработчик задач.
  'audit.exports': ['user.archived'],
  // 020: письмо «Вам открыли доступ к протоколу» или приглашение (research, Р9).
  'delivery.accessLetter': ['protocol.access_granted'],
  // 025: письмо «Восстановление пароля» (research Р4).
  'delivery.recoveryLetter': ['password.recovery_requested'],
  // 030: письмо со ссылкой входа по запросу (research Р4).
  'delivery.loginLinkLetter': ['access.login_link_requested'],
  // 035: приветственное письмо новому пользователю (contracts/api.md 035).
  'delivery.welcomeLetter': ['user.welcome_requested'],
  // 030: смена связи упоминания меняет ответственного в пункте (research Р7, FR-045).
  'minutes.relink': ['directory.links_changed'],
  // 030: склейка, архив записи, выключение «получает письма» — связь учётной записи и отзыв
  // ссылок и сеансов по ссылке (research Р1).
  'access.personLinks': [
    'directory.records_merged',
    'directory.record_archived',
    'directory.org_mail_off',
  ],
  // 031: уведомления о пунктах по ревизии протокола (research Р1).
  'control.itemNotices': ['minutes.revision_applied'],
};

/** Подписчики события — каждому своя задача доставки (FR-022, FR-023). */
export function subscribersOf(name: EventName): EventSubscriber[] {
  return eventSubscribers.filter((subscriber) => {
    const events = eventSubscriptions[subscriber];
    return events === '*' || events.includes(name);
  });
}

/** Проверка данных события перед записью: лишние поля (ФИО, почта) отклоняются. */
export function parseEventData<N extends EventName>(
  name: N,
  data: unknown,
): z.ZodSafeParseResult<EventData<N>> {
  const schema = eventCatalog[name].data as unknown as z.ZodType<EventData<N>>;
  return schema.safeParse(data);
}
